import { useEffect, useState } from "react";
import { ICandidate } from "@src/shared/types";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import { Tag } from "@src/shared/components/Tag/Tag";
import Avatar from "@mui/material/Avatar";
import nextСandidateImg from "@src/shared/assets/arrow_candidates.svg";
import { getWordForm, yearsForms } from "@src/shared/utils/monthToYears";
import { IconButton } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { useSetCandidateRating } from "@src/entities/rating";

import styles from "./HeaderCandidate.module.scss";

type TCandidateRating = "like" | "dislike" | null;

type TProps = {
  searchCandidateResult: ICandidate;
  searchRequestResult: ICandidate[];
  isOverlay: boolean;
};

export const HeaderCandidate = ({
  searchCandidateResult,
  searchRequestResult,
  isOverlay,
}: TProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const candidateId =
    searchParams.get("candidate_id") ?? searchCandidateResult.id;

  const requestUuid = searchParams.get("request_uuid") ?? "";

  const [rating, setRating] = useState<TCandidateRating>(
    searchCandidateResult.rating ?? null,
  );

  const { mutateAsync: setCandidateRating, isPending } =
    useSetCandidateRating();

  const candidateAgeLabel = getWordForm(searchCandidateResult?.age, yearsForms);

  const currentIndex = searchRequestResult.findIndex(
    (item) => item.id === candidateId,
  );

  useEffect(() => {
    setRating(searchCandidateResult.rating ?? null);
  }, [searchCandidateResult.id, searchCandidateResult.rating]);

  const openCandidatProfile = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const renderNeedAtention = () =>
    searchCandidateResult?.warnings.length ? (
      searchCandidateResult.warnings.map((warning) => (
        <div key={warning}>{warning}</div>
      ))
    ) : (
      <div>Отсутствуют данные по данному блоку</div>
    );

  const handleNextCandidate = () => {
    if (!searchRequestResult.length) {
      return;
    }

    const nextIndex = (currentIndex + 1) % searchRequestResult.length;

    const nextCandidate = searchRequestResult[nextIndex];

    if (nextCandidate) {
      navigate(
        `/candidate?request_uuid=${requestUuid}&candidate_id=${nextCandidate.id}`,
      );
    }
  };

  const handleRatingChange = async (
    newRating: Exclude<TCandidateRating, null>,
  ) => {
    if (!requestUuid || !candidateId || isPending || rating === newRating) {
      return;
    }

    const previousRating = rating;

    setRating(newRating);

    try {
      await setCandidateRating({
        requestUuid,
        candidateId,
        rating: newRating,
      });
    } catch {
      setRating(previousRating);
    }
  };

  const isShowNeedAtentionInfo = !!searchCandidateResult?.warnings.length;

  return (
    <div className={clsx(styles.header, isOverlay && styles.overlayHeader)}>
      <div className={styles.candidate}>
        <div className={styles.candidateIcon}>
          {searchCandidateResult?.gender === "Мужской" ? (
            <MaleIcon fontSize="large" sx={{ color: "#00C195" }} />
          ) : (
            <FemaleIcon fontSize="large" sx={{ color: "#00C195" }} />
          )}
        </div>

        <div className={styles.candidateTitle}>
          <div className={styles.candidateTitleText}>
            {searchCandidateResult?.desired_position || "-"}

            {!!searchCandidateResult?.age && (
              <>
                , {searchCandidateResult.age} {candidateAgeLabel}
              </>
            )}
          </div>
          <div className={styles.candidateTag}>
            <Tag tagText={searchCandidateResult?.city || "-"} />
          </div>
        </div>

        <div className={styles.salaryAndWarningsWrap}>
          <div className={styles.salaryInfo}>
            <div className={styles.salaryInfoSections}>
              <div className={styles.avatar}>
                <Avatar
                  className={styles.linkAvatar}
                  onClick={() =>
                    openCandidatProfile(searchCandidateResult?.resume_link)
                  }
                  sx={{ backgroundColor: "#FF0002" }}
                >
                  hh
                </Avatar>
              </div>

              <div className={styles.salary}>
                {searchCandidateResult?.salary || "-"}
              </div>
            </div>
          </div>

          {isShowNeedAtentionInfo && (
            <div className={styles.sectionAtention}>
              <div className={styles.needAtentionTitleCricle} />

              <div className={styles.needAtentionTitleText}>
                Требует внимания
              </div>
            </div>
          )}
          {!isOverlay && (
            <div className={styles.ratingActions}>
              <IconButton
                aria-label="Поставить лайк кандидату"
                disabled={isPending}
                onClick={() => handleRatingChange("like")}
                className={clsx(
                  styles.ratingButton,
                  rating === "like" && styles.likeButtonActive,
                )}
              >
                <ThumbUpOutlinedIcon />
              </IconButton>

              <IconButton
                aria-label="Поставить дизлайк кандидату"
                disabled={isPending}
                onClick={() => handleRatingChange("dislike")}
                className={clsx(
                  styles.ratingButton,
                  rating === "dislike" && styles.dislikeButtonActive,
                )}
              >
                <ThumbDownOutlinedIcon />
              </IconButton>
            </div>
          )}
        </div>
      </div>

      {!isOverlay && (
        <div className={styles.needAtentionWrap}>
          {isShowNeedAtentionInfo && (
            <div className={styles.needAtention}>
              <div className={styles.needAtentionTitle}>
                <div className={styles.needAtentionTitleCricle} />

                <div className={styles.needAtentionTitleText}>
                  Требует внимания
                </div>
              </div>

              <div className={styles.needAtentionFactors}>
                {renderNeedAtention()}
              </div>
            </div>
          )}

          <IconButton
            onClick={handleNextCandidate}
            edge="end"
            className={styles.nextCandidateBtn}
          >
            <img src={nextСandidateImg} alt="Следующий кандидат" />
          </IconButton>
        </div>
      )}
    </div>
  );
};
